import * as d3 from "d3"

import { Size, Margin, Rectangle, ChartRow } from "@/app/report/common/constructor"
import {
  getDecimalPointForTick,
  getLocaleString,
  getMetricValueStr,
  getTicks,
  TypeChecker,
} from "@/app/report/common/tools"
import { colors } from "@/components/colors"
import { TYPOGRAPHY } from "@/components/typography"
import i18n from "@/i18n"
import { isEmpty } from "lodash"

export class Space {
  xAxisLabel: number
  yAxisLabel: number
  yAxisTickAndSpace = 9 // Y축 tick 크기(6) + 라벨과의 간격(3)

  constructor(xAxisLabel: number, yAxisLabel: number) {
    this.xAxisLabel = xAxisLabel
    this.yAxisLabel = yAxisLabel
  }
}

export class Axis {
  name: string
  decimalPoint: number
  unit: string
  manualScaleActive?: boolean
  manualScaleValue?: number | null

  constructor(
    name: string,
    decimalPoint: number,
    unit = "",
    manualScaleActive?: boolean,
    manualScaleValue?: number | null,
  ) {
    this.name = name
    this.decimalPoint = decimalPoint
    this.unit = unit
    this.manualScaleActive = manualScaleActive
    this.manualScaleValue = manualScaleValue
  }
}

export const INDEX_AXIS = {
  X: 0,
  Y: 1,
}

export class ChartStyle {
  magnification: number // 확대 배율

  // Size and Margin
  size: Size
  margin: Margin
  space: Space
  rectAreaChart: Rectangle
  gridStrokeWidth: number

  // Color
  colorAverageAboveBase: string
  colorAverageBelowBase: string
  colorAverageAboveText: string
  colorAverageBelowText: string
  colorAxis: string
  colorAverageAxis: string
  colorTickText: string

  // Flags
  flagShowGridX: boolean
  flagShowGridY: boolean
  flagShowTickStroke: boolean

  // Font Style
  fontFamily: string
  fontSize: number
  fontSizeAxis: number
  fontWeight: number
  fontSizeAvg: number
  fontSizeNoData: number
  fontWeightNoData: number

  // Circle Style
  r: number
  border: number
  sizeOfBorderHighlight: number

  // Axis
  axisX: Axis | null
  axisY: Axis | null

  // Ticks
  ticksMax: number

  constructor({
    width,
    height,
    color,
    magnification = 1.0,
  }: {
    width: number
    height: number
    color: string
    magnification?: number
  }) {
    if (width === 0) {
      width = 600
    }

    if (height === 0) {
      height = 480
    }

    if (magnification < 0.1) {
      magnification = 0.1
    }

    if (magnification > 10.0) {
      magnification = 10.0
    }

    this.magnification = magnification

    this.size = new Size(width * magnification, height * magnification)
    this.margin = new Margin(
      20 * magnification,
      20 * magnification,
      12 * magnification,
      8 * magnification,
    )
    this.space = new Space(44 * magnification, 16 * magnification)
    this.rectAreaChart = new Rectangle({ x: 0, y: 0, width: 0, height: 0 })
    this.gridStrokeWidth = 1.0 * magnification

    this.colorAverageAboveBase = color
    this.colorAverageBelowBase = colors.neutral.grey["600"]

    this.colorAverageAboveText = colors.neutral.white
    this.colorAverageBelowText = colors.neutral.grey["800"]

    this.colorAxis = colors.neutral.grey["1000"]
    this.colorAverageAxis = color
    this.colorTickText = colors.neutral.grey["600"]

    this.flagShowGridX = true
    this.flagShowGridY = true
    this.flagShowTickStroke = true

    this.fontFamily = TYPOGRAPHY.FONT_FAMILY_DEFAULT
    this.fontSize = 14 * magnification
    this.fontWeight = 700 * magnification
    this.fontSizeAxis = 11 * magnification
    this.fontSizeAvg = 11 * magnification
    this.fontSizeNoData = 12 * magnification
    this.fontWeightNoData = 400 * magnification

    this.r = 14 * magnification
    this.border = 2 * magnification
    this.sizeOfBorderHighlight = 4 * magnification

    this.axisX = null
    this.axisY = null

    this.ticksMax = 5
  }
}

export class Chart {
  axisX: Axis | undefined
  axisY: Axis | undefined
  rows: Array<ChartRow> | undefined

  svg: d3.Selection<SVGSVGElement, undefined, null, undefined> | undefined
  tooltip: d3.Selection<HTMLDivElement, undefined, null, undefined> | undefined

  avgX: number | undefined
  avgY: number | undefined
  axisAvgX: d3.Selection<SVGLineElement, number, null, undefined> | undefined
  axisAvgY: d3.Selection<SVGLineElement, number, null, undefined> | undefined
  axisAvgXText: d3.Selection<SVGTextElement, undefined, null, undefined> | undefined
  axisAvgYText: d3.Selection<SVGTextElement, undefined, null, undefined> | undefined

  circlesOutline:
    | d3.Selection<d3.BaseType | SVGCircleElement, ChartRow, SVGGElement, undefined>
    | undefined
  circles:
    | d3.Selection<d3.BaseType | SVGCircleElement, ChartRow, SVGGElement, undefined>
    | undefined

  constructor({
    axisX,
    axisY,
    rows,
    chartStyle,
    mouseoverAxisXAvg,
    mouseoverAxisYAvg,
    mouseoutAxisXYAvg,
    mouseover,
    mousemove,
    mouseout,
    translateKey,
  }: {
    axisX: Axis
    axisY: Axis
    rows: Array<ChartRow>
    chartStyle: ChartStyle
    mouseoverAxisXAvg?: (e: MouseEvent, value: number) => void
    mouseoverAxisYAvg?: (e: MouseEvent, value: number) => void
    mouseoutAxisXYAvg?: () => void
    mouseover?: (e: MouseEvent, row: ChartRow) => void
    mousemove?: (e: MouseEvent) => void
    mouseout?: (e: MouseEvent, row: ChartRow) => void
    translateKey?: (key: string) => string | undefined
  }) {
    this.axisX = axisX
    this.axisY = axisY
    this.rows = rows

    this.avgX = 0
    this.avgY = 0

    const flagEmpty = !this.rows || this.rows.length === 0

    if (flagEmpty) {
      chartStyle.flagShowTickStroke = false
    }

    {
      this.svg = d3
        .create("svg")
        .attr("width", chartStyle.size.width)
        .attr("height", chartStyle.size.height)
        .attr("id", "graph")
        .attr("viewBox", [0, 0, chartStyle.size.width, chartStyle.size.height])
        .attr("style", `max-width: ${chartStyle.size.width}px; height: auto; overflow: visible;`)
    }

    const xMax: number = this.rows.reduce(
      (acc: number, cur: ChartRow) => Math.max(acc, cur.data[INDEX_AXIS.X] || 0),
      0,
    )
    const xDecimalPointTick = getDecimalPointForTick(axisX.decimalPoint, xMax, chartStyle.ticksMax)

    const yMax: number = this.rows.reduce(
      (acc: number, cur: ChartRow) => Math.max(acc, cur.data[INDEX_AXIS.Y] || 0),
      0,
    )
    const yDecimalPointTick = getDecimalPointForTick(axisY.decimalPoint, yMax, chartStyle.ticksMax)

    {
      const valueString = getLocaleString(Number(yMax), yDecimalPointTick)
      const numbersOnly = valueString.replace(/\D/g, "")
      const width =
        numbersOnly.length * chartStyle.fontSizeAxis +
        (valueString.length - numbersOnly.length) * chartStyle.fontSizeAxis * 0.1

      chartStyle.space.yAxisLabel += chartStyle.space.yAxisTickAndSpace + width

      chartStyle.rectAreaChart.x = chartStyle.margin.left + chartStyle.space.yAxisLabel
      chartStyle.rectAreaChart.y = chartStyle.margin.top
      chartStyle.rectAreaChart.width =
        chartStyle.size.width -
        chartStyle.margin.left -
        chartStyle.margin.right -
        chartStyle.space.yAxisLabel
      chartStyle.rectAreaChart.height =
        chartStyle.size.height -
        chartStyle.margin.top -
        chartStyle.margin.bottom -
        chartStyle.space.xAxisLabel
    }

    const xDomainMax =
      axisX.manualScaleActive && axisX.manualScaleValue ? axisX.manualScaleValue : xMax
    const domainX = d3
      .scaleLinear()
      .domain([0, xDomainMax])
      .nice()
      .range([chartStyle.rectAreaChart.left, chartStyle.rectAreaChart.right])

    const yDomainMax =
      axisY.manualScaleActive && axisY.manualScaleValue ? axisY.manualScaleValue : yMax
    const domainY = d3
      .scaleLinear()
      .domain([0, yDomainMax])
      .nice()
      .range([chartStyle.rectAreaChart.bottom, chartStyle.rectAreaChart.top])

    this.svg
      .append("line")
      .attr("x1", chartStyle.rectAreaChart.left)
      .attr("x2", chartStyle.rectAreaChart.right)
      .attr("y1", chartStyle.rectAreaChart.bottom)
      .attr("y2", chartStyle.rectAreaChart.bottom)
      .attr("stroke", colors.neutral.grey["1000"])

    if (!flagEmpty) {
      this.svg
        .append("g")
        .attr("transform", `translate(0, ${chartStyle.rectAreaChart.bottom})`)
        .attr("stroke-width", chartStyle.gridStrokeWidth)
        .call(
          d3
            .axisBottom(domainX)
            .ticks(getTicks(axisX.decimalPoint, xMax, chartStyle.ticksMax))
            .tickFormat((x) => getLocaleString(Number(x), xDecimalPointTick)),
        )
        .call((g) => g.select(".domain").remove())
        .style("fill", chartStyle.colorTickText)
        .style("font-family", chartStyle.fontFamily)
        .style("font-size", chartStyle.fontSizeAxis)
        .call((g) => {
          if (!chartStyle.flagShowTickStroke) {
            g.selectAll(".tick line").remove()
          }
        })

      if (!flagEmpty && chartStyle.flagShowGridX) {
        this.svg
          .selectAll("xGrid")
          .data(domainX.ticks())
          .join("line")
          .attr("x1", (_) => domainX(_))
          .attr("x2", (_) => domainX(_))
          .attr("y1", chartStyle.rectAreaChart.top)
          .attr("y2", chartStyle.rectAreaChart.bottom)
          .style("stroke", chartStyle.colorAxis)
          .style("stroke-opacity", (_, t) => (t === 0 ? 1.0 : 0.2))
          .style("stroke-width", chartStyle.gridStrokeWidth / 2)
      }
    }

    let xAxisLabel = (translateKey && translateKey(this.axisX.name || "")) || ""

    if (axisX.unit) {
      xAxisLabel += ` (${axisX.unit})`
    }
    this.svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("y", chartStyle.rectAreaChart.bottom + chartStyle.space.xAxisLabel)
      .attr("x", chartStyle.rectAreaChart.cx)
      .style("fill", chartStyle.colorTickText)
      .style("font-family", chartStyle.fontFamily)
      .style("font-size", chartStyle.fontSizeAxis)
      .text(xAxisLabel)

    this.svg
      .append("line")
      .attr("x1", chartStyle.rectAreaChart.left)
      .attr("x2", chartStyle.rectAreaChart.left)
      .attr("y1", chartStyle.rectAreaChart.top)
      .attr("y2", chartStyle.rectAreaChart.bottom)
      .attr("stroke", colors.neutral.grey["1000"])

    if (!flagEmpty) {
      this.svg
        .append("g")
        .attr("transform", `translate(${chartStyle.rectAreaChart.left}, 0)`)
        .attr("stroke-width", chartStyle.gridStrokeWidth)
        .call(
          d3
            .axisLeft(domainY)
            .ticks(getTicks(axisY.decimalPoint, yMax, chartStyle.ticksMax))
            .tickFormat((y) => getLocaleString(Number(y), yDecimalPointTick)),
        )
        .call((g) => g.select(".domain").remove())
        .style("fill", chartStyle.colorTickText)
        .style("font-family", chartStyle.fontFamily)
        .style("font-size", chartStyle.fontSizeAxis)
        .call((g) => {
          if (!chartStyle.flagShowTickStroke) {
            g.selectAll(".tick line").remove()
          }
        })

      if (!flagEmpty && chartStyle.flagShowGridY) {
        this.svg
          .selectAll("yGrid")
          .data(domainY.ticks())
          .join("line")
          .attr("x1", chartStyle.rectAreaChart.left)
          .attr("x2", chartStyle.rectAreaChart.right)
          .attr("y1", (d) => domainY(d))
          .attr("y2", (d) => domainY(d))
          .style("stroke", chartStyle.colorAxis)
          .style("stroke-opacity", (_, t) => (t === 0 ? 1.0 : 0.2))
          .style("stroke-width", chartStyle.gridStrokeWidth / 2)
      }
    }

    let axisYLabel = (translateKey && translateKey(this.axisY.name || "")) || ""

    if (axisY.unit) {
      axisYLabel += ` (${axisY.unit})`
    }
    this.svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("transform", "rotate(-90)")
      .attr("y", chartStyle.margin.left + chartStyle.fontSizeAxis)
      .attr("x", -chartStyle.rectAreaChart.cy)
      .style("fill", chartStyle.colorTickText)
      .style("font-family", chartStyle.fontFamily)
      .style("font-size", chartStyle.fontSizeAxis)
      .text(axisYLabel)

    if (
      !isEmpty(this.rows) &&
      TypeChecker.isString(this.axisX.name) &&
      TypeChecker.isString(this.axisY.name)
    ) {
      this.avgX = d3.mean(this.rows, (row) => row.data[INDEX_AXIS.X])
      this.avgY = d3.mean(this.rows, (row) => row.data[INDEX_AXIS.Y])

      if (TypeChecker.isNumber(this.avgX) && TypeChecker.isNumber(this.avgY)) {
        const axisAvgXVal = getMetricValueStr({
          value: this.avgX,
          decimalPoint: axisX.decimalPoint,
          unit: axisX.unit,
          calcType: "total",
          metric: axisX.name.split(".")[1],
        })

        const axisAvgYVal = getMetricValueStr({
          value: this.avgY,
          decimalPoint: axisY.decimalPoint,
          unit: axisY.unit,
          calcType: "total",
          metric: axisY.name.split(".")[1],
        })

        this.axisAvgXText = this.svg
          .append("text")
          .attr("x", domainX(this.avgX) + chartStyle.fontSizeAvg / 2)
          .attr("y", chartStyle.rectAreaChart.bottom - chartStyle.fontSizeAvg)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "middle")
          .style("font-family", chartStyle.fontFamily)
          .style("font-size", chartStyle.fontSizeAvg)
          .style("font-weight", chartStyle.fontWeight)
          .style("fill", chartStyle.colorAverageAxis)
          .style("fill-opacity", 0.5)
          .text(`Avg. ${axisAvgXVal}`)

        this.axisAvgYText = this.svg
          .append("text")
          .attr("x", chartStyle.rectAreaChart.right - chartStyle.fontSizeAvg / 2)
          .attr("y", domainY(this.avgY) - chartStyle.fontSizeAvg)
          .attr("text-anchor", "end")
          .attr("dominant-baseline", "middle")
          .style("font-family", chartStyle.fontFamily)
          .style("font-size", chartStyle.fontSizeAvg)
          .style("font-weight", chartStyle.fontWeight)
          .style("fill", chartStyle.colorAverageAxis)
          .style("fill-opacity", 0.5)
          .text(`Avg. ${axisAvgYVal}`)

        this.axisAvgX = this.svg
          .append("line")
          .datum(this.avgX)
          .attr("x1", domainX(this.avgX))
          .attr("x2", domainX(this.avgX))
          .attr("y1", chartStyle.rectAreaChart.top)
          .attr("y2", chartStyle.rectAreaChart.bottom)
          .style("stroke", chartStyle.colorAverageAxis)
          .style("stroke-width", chartStyle.gridStrokeWidth * 2)
          .style("stroke-opacity", 0.5)

        this.axisAvgY = this.svg
          .append("line")
          .datum(this.avgY)
          .attr("x1", chartStyle.rectAreaChart.left)
          .attr("x2", chartStyle.rectAreaChart.right)
          .attr("y1", domainY(this.avgY))
          .attr("y2", domainY(this.avgY))
          .style("stroke", chartStyle.colorAverageAxis)
          .style("stroke-width", chartStyle.gridStrokeWidth * 2)
          .style("stroke-opacity", 0.5)

        const avgXLine = this.svg
          .append("line")
          .datum(this.avgX)
          .attr("x1", domainX(this.avgX))
          .attr("x2", domainX(this.avgX))
          .attr("y1", chartStyle.rectAreaChart.top)
          .attr("y2", chartStyle.rectAreaChart.bottom)
          .style("stroke", "white")
          .style("stroke-width", 20 * chartStyle.magnification)
          .style("opacity", 0)

        if (mouseoverAxisXAvg) {
          avgXLine.on("mouseover", mouseoverAxisXAvg)
        }

        if (mousemove) {
          avgXLine.on("mousemove", mousemove)
        }

        if (mouseoutAxisXYAvg) {
          avgXLine.on("mouseout", mouseoutAxisXYAvg)
        }

        const avgYLine = this.svg
          .append("line")
          .datum(this.avgY)
          .attr("x1", chartStyle.rectAreaChart.left)
          .attr("x2", chartStyle.rectAreaChart.right)
          .attr("y1", domainY(this.avgY))
          .attr("y2", domainY(this.avgY))
          .style("stroke", "white")
          .style("stroke-width", 20 * chartStyle.magnification)
          .style("opacity", 0)

        if (mouseoverAxisYAvg) {
          avgYLine.on("mouseover", mouseoverAxisYAvg)
        }

        if (mousemove) {
          avgYLine.on("mousemove", mousemove)
        }

        if (mouseoutAxisXYAvg) {
          avgYLine.on("mouseout", mouseoutAxisXYAvg)
        }
      }
    }

    if (
      !isEmpty(this.rows) &&
      TypeChecker.isNumber(this.avgX) &&
      TypeChecker.isNumber(this.avgY) &&
      !isEmpty(this.axisX.name) &&
      !isEmpty(this.axisY.name)
    ) {
      this.circlesOutline = this.svg
        .append("g")
        .selectAll("circle")
        .data(this.rows)
        .join("circle")
        .attr("cx", (row) => domainX(row.data[INDEX_AXIS.X] || 0))
        .attr("cy", (row) => domainY(row.data[INDEX_AXIS.Y] || 0))
        .attr("r", chartStyle.r + chartStyle.border)
        .style("fill-opacity", 0)
        .style("stroke", (row) =>
          this.areTheValuesAboveAverage(row)
            ? chartStyle.colorAverageAboveBase
            : chartStyle.colorAverageBelowBase,
        )
        .style("stroke-width", 4 * chartStyle.magnification)
        .style("stroke-opacity", 0)

      this.circles = this.svg
        .append("g")
        .selectAll("circle")
        .data(this.rows)
        .join("circle")
        .attr("cx", (row) => domainX(row.data[INDEX_AXIS.X] || 0))
        .attr("cy", (row) => domainY(row.data[INDEX_AXIS.Y] || 0))
        .attr("r", chartStyle.r)
        .style("fill", (row) =>
          this.areTheValuesAboveAverage(row)
            ? chartStyle.colorAverageAboveBase
            : chartStyle.colorAverageBelowBase,
        )
        .style("fill-opacity", (row) => (this.areTheValuesAboveAverage(row) ? 1.0 : 0.2))
        .style("stroke", (row) =>
          this.areTheValuesAboveAverage(row)
            ? chartStyle.colorAverageAboveBase
            : chartStyle.colorAverageBelowBase,
        )
        .style("stroke-width", chartStyle.border)

      if (mouseover) {
        this.circles.on("mouseover", mouseover)
      }

      if (mousemove) {
        this.circles.on("mousemove", mousemove)
      }

      if (mouseout) {
        this.circles.on("mouseout", mouseout)
      }

      const text = this.svg
        .append("g")
        .selectAll("text")
        .data(this.rows)
        .enter()
        .append("text")
        .attr("x", (row) => domainX(row.data[INDEX_AXIS.X] || 0))
        .attr("y", (row) => domainY(row.data[INDEX_AXIS.Y] || 0))
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "central")
        .style("fill", (row) =>
          this.areTheValuesAboveAverage(row)
            ? chartStyle.colorAverageAboveText
            : chartStyle.colorAverageBelowText,
        )
        .style("font-family", chartStyle.fontFamily)
        .style("font-size", chartStyle.fontSize)
        .style("font-weight", chartStyle.fontWeight)
        .text((row) => row.name)

      if (mouseover) {
        text.on("mouseover", mouseover)
      }

      if (mousemove) {
        text.on("mousemove", mousemove)
      }

      if (mouseout) {
        text.on("mouseout", mouseout)
      }
    }
    if (flagEmpty) {
      this.svg
        .append("text")
        .attr("text-anchor", "middle")
        .attr("x", chartStyle.rectAreaChart.left + chartStyle.rectAreaChart.width / 2)
        .attr("y", chartStyle.rectAreaChart.top + chartStyle.rectAreaChart.height / 2)
        .style("fill", colors.neutral.grey["500"])
        .style("font-family", chartStyle.fontFamily)
        .style("font-weight", chartStyle.fontWeightNoData)
        .style("font-size", chartStyle.fontSizeNoData)
        .text(i18n.t("messages.noDataAvailable").toString())
    }
  }

  areTheValuesAboveAverage(row: ChartRow) {
    if (
      !TypeChecker.isNumber(this.avgX) ||
      !TypeChecker.isNumber(this.avgY) ||
      !this.axisX ||
      !this.axisY
    ) {
      return false
    }

    return this.avgX < (row.data[INDEX_AXIS.X] || 0) && this.avgY < (row.data[INDEX_AXIS.Y] || 0)
  }

  setAxisAvgXOpacity(opacity: number) {
    if (this.axisAvgX) {
      this.axisAvgX.style("stroke-opacity", opacity)
    }

    if (this.axisAvgXText) {
      this.axisAvgXText.style("fill-opacity", opacity)
    }
  }

  setAxisAvgYOpacity(opacity: number) {
    if (this.axisAvgY) {
      this.axisAvgY.style("stroke-opacity", opacity)
    }

    if (this.axisAvgYText) {
      this.axisAvgYText.style("fill-opacity", opacity)
    }
  }

  setHighlightCircle(_: MouseEvent, chartRow: ChartRow, flagHighlight: boolean) {
    if (!this.circles || !this.circlesOutline) {
      return
    }

    const isAboveAverage = this.areTheValuesAboveAverage(chartRow)

    let circleOpacity = 0
    let circleOutlineOpacity = 0

    if (flagHighlight) {
      circleOpacity = isAboveAverage ? 0.8 : 0.2
      circleOutlineOpacity = isAboveAverage ? 0.5 : 0.2
    } else {
      circleOpacity = isAboveAverage ? 1.0 : 0.2
    }

    this.circles.filter((p) => this.compareValues(p, chartRow)).style("fill-opacity", circleOpacity)
    this.circlesOutline
      .filter((p) => this.compareValues(p, chartRow))
      .style("stroke-opacity", circleOutlineOpacity)
  }

  compareValues(source: ChartRow, target: ChartRow): boolean {
    return (
      source.name === target.name &&
      source.data[0] === target.data[0] &&
      source.data[1] === target.data[1]
    )
  }
}
